import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Handle, Position, type NodeProps, useUpdateNodeInternals } from '@xyflow/react';
import { useNodeResize } from './useNodeResize';
import { getEstimatedCredits } from '@shared/billing/costEstimator';
import { SlidersHorizontal, FileText, Video, X, Volume2 } from 'lucide-react';
import { SmartModelSelector } from './shared/SmartModelSelector';
import { NODE_SELECT_CLASS, NODE_INPUT_CLASS, NODE_LABEL_CLASS, NODE_WARNING_CLASS } from './shared/nodeStyles';

interface SettingField {
  key: string;
  label: string;
  type: 'select' | 'slider' | 'toggle';
  options?: Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
  step?: number;
  default: any;
}

interface ImageSizeOption {
  value: string;
  label: string;
}

interface ImageModelDefinition {
  id: string;
  name: string;
  provider: string;
  description?: string;
  supportsReferenceImages: boolean;
  maxReferenceImages?: number;
  requiresReferenceImage?: boolean;
  sizeOptions?: ImageSizeOption[];
  aspectRatioOptions?: string[];
  settings: SettingField[];
  defaultSize?: string;
  defaultAspectRatio?: string;
  supportsCustomDimensions?: boolean;
  costPerImage: number;
}

interface VideoModelDefinition {
  id: string;
  name: string;
  provider: string;
  description?: string;
  supportsReferenceImages: boolean;
  maxReferenceImages: number;
  requiresReferenceImage: boolean;
  durationOptions: Array<{ value: string; label: string }>;
  resolutionOptions: Array<{ value: string; label: string }>;
  aspectRatioOptions: Array<{ value: string; label: string }>;
  defaultDuration: string;
  defaultResolution: string;
  defaultAspectRatio: string;
  supportsAudio: boolean;
  settings: SettingField[];
  costPerSecond: number;
  mediaType: 'video';
}

interface TextModelDefinition {
  id: string;
  name: string;
  provider: string;
  description?: string;
  maxOutputTokens: number;
  supportsSystemPrompt: boolean;
  settings: SettingField[];
  costPer1kTokens: { input: number; output: number };
}

const DEFAULT_IMAGE_MODEL_ID = 'gemini-2.5-flash-image';
const DEFAULT_VIDEO_MODEL_ID = 'seedance-1.5-t2v';
const DEFAULT_TEXT_MODEL_ID = 'gemini-3-flash-preview';

function pickDefaultModel<T extends { id: string }>(models: T[], defaultId: string): T | undefined {
  return models.find((model) => model.id === defaultId) || models[0];
}

// Cache models globally so we don't refetch on every node render
let cachedModels: ImageModelDefinition[] | null = null;
let fetchPromise: Promise<ImageModelDefinition[]> | null = null;
let cachedVideoModels: VideoModelDefinition[] | null = null;
let videoFetchPromise: Promise<VideoModelDefinition[]> | null = null;
let cachedTextModels: TextModelDefinition[] | null = null;
let textFetchPromise: Promise<TextModelDefinition[]> | null = null;

// Expose the cache for other components (e.g., GenerateNode ref image warnings)
export function getImageModelsCache(): ImageModelDefinition[] | null {
  if (!cachedModels && !fetchPromise) fetchModels(); // trigger fetch if not started
  return cachedModels;
}

export function getVideoModelsCache(): VideoModelDefinition[] | null {
  if (!cachedVideoModels && !videoFetchPromise) fetchVideoModels();
  return cachedVideoModels;
}

export function getTextModelsCache(): TextModelDefinition[] | null {
  if (!cachedTextModels && !textFetchPromise) fetchTextModels();
  return cachedTextModels;
}

function fetchModels(): Promise<ImageModelDefinition[]> {
  if (cachedModels) return Promise.resolve(cachedModels);
  if (fetchPromise) return fetchPromise;
  fetchPromise = fetch('/api/canvases/image-models', { credentials: 'include' })
    .then((res) => res.json())
    .then((data) => {
      cachedModels = data.data || [];
      return cachedModels!;
    })
    .catch(() => {
      fetchPromise = null;
      return [];
    });
  return fetchPromise;
}

function fetchVideoModels(): Promise<VideoModelDefinition[]> {
  if (cachedVideoModels) return Promise.resolve(cachedVideoModels);
  if (videoFetchPromise) return videoFetchPromise;
  videoFetchPromise = fetch('/api/canvases/video-models', { credentials: 'include' })
    .then((res) => res.json())
    .then((data) => {
      cachedVideoModels = data.data || [];
      return cachedVideoModels!;
    })
    .catch(() => {
      videoFetchPromise = null;
      return [];
    });
  return videoFetchPromise;
}

function fetchTextModels(): Promise<TextModelDefinition[]> {
  if (cachedTextModels) return Promise.resolve(cachedTextModels);
  if (textFetchPromise) return textFetchPromise;
  textFetchPromise = fetch('/api/canvases/text-models', { credentials: 'include' })
    .then((res) => res.json())
    .then((data) => {
      cachedTextModels = data.data || [];
      return cachedTextModels!;
    })
    .catch(() => {
      textFetchPromise = null;
      return [];
    });
  return textFetchPromise;
}

interface PresetNodeDataProps {
  mediaType?: 'image' | 'video' | 'text';
  model?: string;
  size?: string;
  aspectRatio?: string;
  duration?: string;
  resolution?: string;
  modelSettings?: Record<string, any>;
  customWidth?: number;
  customHeight?: number;
  nodeWidth?: number;
  onUpdate?: (id: string, updates: any) => void;
  onDelete?: (id: string) => void;
  onWidthChange?: (id: string, width: number) => void;
}

export function PresetNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as PresetNodeDataProps;
  const mediaType: 'image' | 'video' | 'text' = data.mediaType || 'image';
  const [models, setModels] = useState<ImageModelDefinition[]>(cachedModels || []);
  const [videoModels, setVideoModels] = useState<VideoModelDefinition[]>(cachedVideoModels || []);
  const [textModels, setTextModels] = useState<TextModelDefinition[]>(cachedTextModels || []);
  const updateNodeInternals = useUpdateNodeInternals();

  const { nodeWidth, resizeHandleProps } = useNodeResize({
    width: data.nodeWidth as number | undefined,
    minWidth: 200,
    maxWidth: 600,
    onWidthChange: (w) => data.onWidthChange?.(id, w),
  });

  useEffect(() => {
    fetchModels().then(setModels);
    fetchVideoModels().then(setVideoModels);
    fetchTextModels().then(setTextModels);
  }, []);

  // When mediaType or model changes, the node height changes —
  // tell ReactFlow to recalculate handle positions.
  // Use requestAnimationFrame + setTimeout to ensure DOM has fully updated
  // before recalculating (fixes ghost handle issue in Video mode).
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setTimeout(() => {
        updateNodeInternals(id);
      }, 50);
    });
    return () => cancelAnimationFrame(raf);
  }, [id, mediaType, data.model, updateNodeInternals]);

  const handleMediaTypeChange = useCallback(
    (newMediaType: 'image' | 'video' | 'text') => {
      if (data.onUpdate) {
        if (newMediaType === 'video') {
          const firstVideoModel = pickDefaultModel(
            cachedVideoModels || videoModels,
            DEFAULT_VIDEO_MODEL_ID,
          );
          const defaultSettings: Record<string, any> = {};
          if (firstVideoModel) {
            for (const field of firstVideoModel.settings) {
              defaultSettings[field.key] = field.default;
            }
          }
          data.onUpdate(id, {
            data: {
              ...data,
              mediaType: 'video',
              model: firstVideoModel?.id || '',
              duration: firstVideoModel?.defaultDuration || '5',
              resolution: firstVideoModel?.defaultResolution || '720p',
              aspectRatio: firstVideoModel?.defaultAspectRatio || '16:9',
              modelSettings: defaultSettings,
            },
          });
        } else if (newMediaType === 'text') {
          const firstTextModel = pickDefaultModel(
            cachedTextModels || textModels,
            DEFAULT_TEXT_MODEL_ID,
          );
          const defaultSettings: Record<string, any> = {};
          if (firstTextModel) {
            for (const field of firstTextModel.settings) {
              defaultSettings[field.key] = field.default;
            }
          }
          data.onUpdate(id, {
            data: {
              ...data,
              mediaType: 'text',
              model: firstTextModel?.id || '',
              modelSettings: defaultSettings,
            },
          });
        } else {
          const firstModel = pickDefaultModel(cachedModels || models, DEFAULT_IMAGE_MODEL_ID);
          const defaultSettings: Record<string, any> = {};
          if (firstModel) {
            for (const field of firstModel.settings) {
              defaultSettings[field.key] = field.default;
            }
          }
          data.onUpdate(id, {
            data: {
              ...data,
              mediaType: 'image',
              model: firstModel?.id || '',
              size: firstModel?.defaultSize || '1K',
              aspectRatio: firstModel?.defaultAspectRatio || '1:1',
              modelSettings: defaultSettings,
            },
          });
        }
      }
    },
    [id, data, models, videoModels, textModels],
  );

  useEffect(() => {
    if (!data.onUpdate) return;
    if (mediaType === 'video') {
      if (!videoModels.length) return;
      const hasModel = videoModels.some((model) => model.id === data.model);
      if (!hasModel) {
        const defaultModel = pickDefaultModel(videoModels, DEFAULT_VIDEO_MODEL_ID);
        if (!defaultModel) return;
        const defaultSettings: Record<string, any> = {};
        for (const field of defaultModel.settings) {
          defaultSettings[field.key] = field.default;
        }
        data.onUpdate(id, {
          data: {
            ...data,
            model: defaultModel.id,
            duration: defaultModel.defaultDuration || '5',
            resolution: defaultModel.defaultResolution || '720p',
            aspectRatio: defaultModel.defaultAspectRatio || '16:9',
            modelSettings: defaultSettings,
          },
        });
      }
      return;
    }

    if (mediaType === 'text') {
      if (!textModels.length) return;
      const hasModel = textModels.some((model) => model.id === data.model);
      if (!hasModel) {
        const defaultModel = pickDefaultModel(textModels, DEFAULT_TEXT_MODEL_ID);
        if (!defaultModel) return;
        const defaultSettings: Record<string, any> = {};
        for (const field of defaultModel.settings) {
          defaultSettings[field.key] = field.default;
        }
        data.onUpdate(id, {
          data: {
            ...data,
            model: defaultModel.id,
            modelSettings: defaultSettings,
          },
        });
      }
      return;
    }

    if (!models.length) return;
    const hasModel = models.some((model) => model.id === data.model);
    if (!hasModel) {
      const defaultModel = pickDefaultModel(models, DEFAULT_IMAGE_MODEL_ID);
      if (!defaultModel) return;
      const defaultSettings: Record<string, any> = {};
      for (const field of defaultModel.settings) {
        defaultSettings[field.key] = field.default;
      }
      data.onUpdate(id, {
        data: {
          ...data,
          model: defaultModel.id,
          size: defaultModel.defaultSize || '1K',
          aspectRatio: defaultModel.defaultAspectRatio || '1:1',
          customWidth: undefined,
          customHeight: undefined,
          modelSettings: defaultSettings,
        },
      });
    }
  }, [id, data, mediaType, models, videoModels, textModels]);

  const selectedModel = useMemo(
    () => models.find((m) => m.id === data.model) || models[0],
    [models, data.model],
  );

  const handleChange = useCallback(
    (field: string, value: any) => {
      if (data.onUpdate) {
        data.onUpdate(id, { data: { ...data, [field]: value } });
      }
    },
    [id, data],
  );

  const handleSettingChange = useCallback(
    (key: string, value: any) => {
      const currentSettings = data.modelSettings || {};
      if (data.onUpdate) {
        data.onUpdate(id, {
          data: { ...data, modelSettings: { ...currentSettings, [key]: value } },
        });
      }
    },
    [id, data],
  );

  const handleModelChange = useCallback(
    (modelId: string) => {
      if (mediaType === 'video') {
        const newModel = videoModels.find((m) => m.id === modelId);
        if (newModel && data.onUpdate) {
          const defaultSettings: Record<string, any> = {};
          for (const field of newModel.settings) {
            defaultSettings[field.key] = field.default;
          }
          data.onUpdate(id, {
            data: {
              ...data,
              model: modelId,
              duration: newModel.defaultDuration || '5',
              resolution: newModel.defaultResolution || '720p',
              aspectRatio: newModel.defaultAspectRatio || '16:9',
              modelSettings: defaultSettings,
            },
          });
        }
      } else if (mediaType === 'text') {
        const newModel = textModels.find((m) => m.id === modelId);
        if (newModel && data.onUpdate) {
          const defaultSettings: Record<string, any> = {};
          for (const field of newModel.settings) {
            defaultSettings[field.key] = field.default;
          }
          data.onUpdate(id, {
            data: {
              ...data,
              model: modelId,
              modelSettings: defaultSettings,
            },
          });
        }
      } else {
        const newModel = models.find((m) => m.id === modelId);
        if (newModel && data.onUpdate) {
          const defaultSettings: Record<string, any> = {};
          for (const field of newModel.settings) {
            defaultSettings[field.key] = field.default;
          }
          data.onUpdate(id, {
            data: {
              ...data,
              model: modelId,
              size: newModel.defaultSize || '',
              aspectRatio: newModel.defaultAspectRatio || '',
              customWidth: undefined,
              customHeight: undefined,
              modelSettings: defaultSettings,
            },
          });
        }
      }
    },
    [id, data, models, videoModels, textModels, mediaType],
  );

  const selectClass = NODE_SELECT_CLASS;

  function renderSettingField(field: SettingField) {
    const value = data.modelSettings?.[field.key] ?? field.default;
    switch (field.type) {
      case 'select':
        return (
          <select
            className={selectClass}
            value={value}
            onChange={(e) => handleSettingChange(field.key, e.target.value)}
          >
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case 'slider':
        return (
          <div className="flex items-center gap-2">
            <input
              type="range"
              className="flex-1"
              min={field.min}
              max={field.max}
              step={field.step}
              value={value}
              onChange={(e) => handleSettingChange(field.key, Number(e.target.value))}
            />
            <span className="text-xs text-gray-500 min-w-[2rem] text-right font-mono">{value}</span>
          </div>
        );
      case 'toggle':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleSettingChange(field.key, e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-xs text-gray-600">{value ? 'On' : 'Off'}</span>
          </label>
        );
      default:
        return null;
    }
  }

  const selectedVideoModel = useMemo(
    () => videoModels.find((m) => m.id === data.model) || videoModels[0],
    [videoModels, data.model],
  );

  const selectedTextModel = useMemo(
    () => textModels.find((m) => m.id === data.model) || textModels[0],
    [textModels, data.model],
  );

  const activeModels =
    mediaType === 'video' ? videoModels : mediaType === 'text' ? textModels : models;
  const isLoading =
    mediaType === 'video'
      ? videoModels.length === 0
      : mediaType === 'text'
      ? textModels.length === 0
        : models.length === 0;

  const estimatedCredits = useMemo(() => {
    if (!data.model) return null;
    if (mediaType === 'text') return 0;

    return getEstimatedCredits({
      modelId: data.model,
      mediaType,
      size: data.size,
      duration: data.duration,
      resolution: data.resolution,
      audioEnabled: data.modelSettings?.generate_audio !== false,
      modelSettings: data.modelSettings,
    });
  }, [
    data.model,
    data.size,
    data.duration,
    data.resolution,
    data.modelSettings,
    mediaType,
  ]);

  // Determine colors based on media type
  const headerBgClass =
    mediaType === 'text'
      ? 'bg-teal-50 border-b border-teal-100'
      : mediaType === 'video'
        ? 'bg-purple-50 border-b border-purple-100'
        : 'bg-info-50 border-b border-info-100';
  const headerIconClass =
    mediaType === 'text'
      ? 'text-teal-600'
      : mediaType === 'video'
        ? 'text-purple-600'
        : 'text-info-600';
  const headerTextClass =
    mediaType === 'text'
      ? 'text-teal-700'
      : mediaType === 'video'
        ? 'text-purple-700'
        : 'text-info-700';
  const handleColorClass =
    mediaType === 'text'
      ? '!bg-teal-500'
      : mediaType === 'video'
        ? '!bg-purple-500'
        : '!bg-info-500';

  if (isLoading) {
    return (
      <div
        className={`bg-white rounded-lg border-2 shadow-md min-w-[200px] ${selected ? 'border-primary-500' : 'border-gray-200'}`}
      >
        <div className="p-3 text-xs text-gray-400">Loading models...</div>
        <Handle
          type="source"
          position={Position.Right}
          id="preset-source"
          className="!bg-info-500 !w-3 !h-3"
        />
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-md min-w-[200px] relative ${!nodeWidth ? 'max-w-[260px]' : ''} ${selected ? 'border-primary-500' : 'border-gray-200'}`}
      style={nodeWidth ? { width: nodeWidth } : undefined}
    >
      <div className={`px-3 py-2 ${headerBgClass} rounded-t-lg flex items-center gap-2`}>
        {mediaType === 'text' ? (
          <FileText className={`w-4 h-4 ${headerIconClass}`} />
        ) : mediaType === 'video' ? (
          <Video className={`w-4 h-4 ${headerIconClass}`} />
        ) : (
          <SlidersHorizontal className={`w-4 h-4 ${headerIconClass}`} />
        )}
        <span className={`text-xs font-semibold ${headerTextClass}`}>Preset</span>
        {selected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete?.(id);
            }}
            className="ml-auto text-gray-400 hover:text-red-500 p-0.5"
            aria-label="Delete node"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-3 space-y-2">
        {/* Media Type Toggle */}
        <div>
          <label className={NODE_LABEL_CLASS}>Type</label>
          <div className="flex rounded-md overflow-hidden border border-gray-200">
            <button
              className={`flex-1 text-xs py-1 px-2 ${mediaType === 'image' ? 'bg-info-100 text-info-700 font-semibold' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              onClick={() => handleMediaTypeChange('image')}
            >
              Image
            </button>
            <button
              className={`flex-1 text-xs py-1 px-2 ${mediaType === 'video' ? 'bg-purple-100 text-purple-700 font-semibold' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              onClick={() => handleMediaTypeChange('video')}
            >
              Video
            </button>
            <button
              className={`flex-1 text-xs py-1 px-2 ${mediaType === 'text' ? 'bg-teal-100 text-teal-700 font-semibold' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              onClick={() => handleMediaTypeChange('text')}
            >
              Text
            </button>
          </div>
        </div>

        {/* Model selector */}
        <div>
          <label className={NODE_LABEL_CLASS}>Model</label>
          <SmartModelSelector
            mediaType={mediaType}
            selectedModelId={data.model || activeModels[0]?.id || ''}
            onSelect={(modelId) => handleModelChange(modelId)}
            models={activeModels}
          />
        </div>

        {/* IMAGE-specific settings */}
        {mediaType === 'image' && (
          <>
            {/* Size (only if model has sizeOptions) */}
            {selectedModel?.sizeOptions && (
              <div>
                <label className={NODE_LABEL_CLASS}>Size</label>
                <select
                  className={selectClass}
                  value={data.size || selectedModel.defaultSize || ''}
                  onChange={(e) => handleChange('size', e.target.value)}
                >
                  {selectedModel.sizeOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Aspect Ratio */}
            {selectedModel?.aspectRatioOptions && (
              <div>
                <label className={NODE_LABEL_CLASS}>Aspect Ratio</label>
                <select
                  className={selectClass}
                  value={data.aspectRatio || selectedModel.defaultAspectRatio || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== 'custom') {
                      // Clear custom dimensions when switching to a preset ratio
                      if (data.onUpdate) {
                        data.onUpdate(id, {
                          data: {
                            ...data,
                            aspectRatio: val,
                            customWidth: undefined,
                            customHeight: undefined,
                          },
                        });
                      }
                    } else {
                      handleChange('aspectRatio', val);
                    }
                  }}
                >
                  {selectedModel.aspectRatioOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                  {selectedModel.supportsCustomDimensions && <option value="custom">Custom</option>}
                </select>
              </div>
            )}

            {/* Custom Dimensions (when Custom aspect ratio is selected) */}
            {selectedModel?.supportsCustomDimensions && data.aspectRatio === 'custom' && (
              <div>
                <label className={NODE_LABEL_CLASS}>Dimensions (px)</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    className={NODE_INPUT_CLASS}
                    placeholder="Width"
                    min={256}
                    max={4096}
                    value={data.customWidth || ''}
                    onChange={(e) =>
                      handleChange(
                        'customWidth',
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
                  />
                  <span className="text-xs text-gray-400">x</span>
                  <input
                    type="number"
                    className={NODE_INPUT_CLASS}
                    placeholder="Height"
                    min={256}
                    max={4096}
                    value={data.customHeight || ''}
                    onChange={(e) =>
                      handleChange(
                        'customHeight',
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
                  />
                </div>
              </div>
            )}

            {/* Model-specific settings */}
            {selectedModel?.settings.map((field) => (
              <div key={field.key}>
                <label className={NODE_LABEL_CLASS}>{field.label}</label>
                {renderSettingField(field)}
              </div>
            ))}

            {/* Reference image info */}
            {selectedModel?.requiresReferenceImage && (
              <div className={NODE_WARNING_CLASS}>
                Requires reference image(s)
              </div>
            )}
            {selectedModel && (
              <div className="text-xs text-gray-400 mt-1">
                {selectedModel.supportsReferenceImages
                  ? `Ref images: up to ${selectedModel.maxReferenceImages}`
                  : 'No ref image support'}
              </div>
            )}
          </>
        )}

        {/* VIDEO-specific settings */}
        {mediaType === 'video' && selectedVideoModel && (
          <>
            {/* Duration */}
            {selectedVideoModel.durationOptions.length > 0 && (
              <div>
                <label className={NODE_LABEL_CLASS}>Duration</label>
                <select
                  className={selectClass}
                  value={data.duration || selectedVideoModel.defaultDuration}
                  onChange={(e) => handleChange('duration', e.target.value)}
                >
                  {selectedVideoModel.durationOptions.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Resolution */}
            {selectedVideoModel.resolutionOptions.length > 0 && (
              <div>
                <label className={NODE_LABEL_CLASS}>Resolution</label>
                <select
                  className={selectClass}
                  value={data.resolution || selectedVideoModel.defaultResolution}
                  onChange={(e) => handleChange('resolution', e.target.value)}
                >
                  {selectedVideoModel.resolutionOptions.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Aspect Ratio */}
            {selectedVideoModel.aspectRatioOptions.length > 0 && (
              <div>
                <label className={NODE_LABEL_CLASS}>Aspect Ratio</label>
                <select
                  className={selectClass}
                  value={data.aspectRatio || selectedVideoModel.defaultAspectRatio}
                  onChange={(e) => handleChange('aspectRatio', e.target.value)}
                >
                  {selectedVideoModel.aspectRatioOptions.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Model-specific settings */}
            {selectedVideoModel.settings.map((field) => (
              <div key={field.key}>
                <label className={NODE_LABEL_CLASS}>{field.label}</label>
                {renderSettingField(field)}
              </div>
            ))}

            {/* Audio support indicator */}
            {selectedVideoModel.supportsAudio && (
              <div className="text-xs text-green-700 bg-green-50 border border-green-200 p-2 rounded-md flex items-center gap-1">
                <Volume2 className="w-3 h-3" />
                Generates audio
              </div>
            )}

            {/* Reference image info */}
            {selectedVideoModel.requiresReferenceImage && (
              <div className={NODE_WARNING_CLASS}>
                Requires reference image
              </div>
            )}
            {selectedVideoModel.supportsReferenceImages && (
              <div className="text-xs text-gray-400 mt-1">
                Ref images: up to {selectedVideoModel.maxReferenceImages}
              </div>
            )}
          </>
        )}

        {/* TEXT-specific settings */}
        {mediaType === 'text' && selectedTextModel && (
          <>
            {/* Model-specific settings (temperature, max length) */}
            {selectedTextModel.settings.map((field) => (
              <div key={field.key}>
                <label className={NODE_LABEL_CLASS}>{field.label}</label>
                {renderSettingField(field)}
              </div>
            ))}

            {/* Text-specific info */}
            <div className="text-xs text-teal-600 bg-teal-50 p-1.5 rounded flex items-center gap-1">
              <FileText className="w-3 h-3" />
              Output can be used as prompt input
            </div>
          </>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id="preset-source"
        className={`!w-3 !h-3 ${handleColorClass}`}
      />
      <div {...resizeHandleProps} />
    </div>
  );
}
